import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaFileAlt, FaEnvelope, FaUserFriends, FaImages } from "react-icons/fa";

import AdminShell from "../../Components/admin/AdminShell";
import StatCard from "../../Components/admin/StatCard";
import RecentArticles from "../../Components/admin/RecentArticles";
import DashboardMessagesCard from "../../Components/admin/DashboardMessagesCard";
import DashboardVolunteersCard from "../../Components/admin/DashboardVolunteersCard";

import { getApiBase } from "../../services/apiConfig";
import { deleteEvent, EventApiError } from "../../services/eventsApi";
import { fetchAdminDashboardBundle } from "../../services/adminApi";

const statIcons = {
  posts: <FaFileAlt />,
  messages: <FaEnvelope />,
  volunteers: <FaUserFriends />,
  media: <FaImages />,
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState([]);
  const [articles, setArticles] = useState([]);
  const [messagePreview, setMessagePreview] = useState([]);
  const [volunteerPreview, setVolunteerPreview] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setError("");
      setLoading(true);

      try {
        const bundle = await fetchAdminDashboardBundle();
        if (cancelled) return;
        setStats(bundle.stats);
        setArticles(bundle.articles);
        setMessagePreview(bundle.messagePreview);
        setVolunteerPreview(bundle.volunteerPreview);
      } catch (e) {
        if (cancelled) return;
        setError("Failed to load admin data. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const statsContent = useMemo(() => {
    if (loading) {
      return [0, 1, 2, 3].map((i) => (
        <div key={i} className="adminCard adminStatCard" aria-hidden="true">
          <div className="adminStatTop">
            <div style={{ width: "100%" }}>
              <div className="adminSkeleton" style={{ width: "55%" }} />
              <div className="adminSkeleton" style={{ width: "35%", marginTop: 10, height: 22 }} />
            </div>
            <div className="adminStatIcon">
              <div className="adminSkeleton" style={{ width: 18, height: 18 }} />
            </div>
          </div>
          <div className="adminSkeleton" style={{ width: "65%", marginTop: 12 }} />
        </div>
      ));
    }

    return (stats || []).map((s) => (
      <StatCard
        key={s.key}
        title={s.title}
        value={s.value}
        trend={s.trend}
        trendDirection={s.trendDirection}
        icon={statIcons[s.key] || <FaFileAlt />}
      />
    ));
  }, [loading, stats]);

  const handleArticleAction = async (type, article) => {
    if (type === "delete") {
      if (!getApiBase()) return;
      const ok = window.confirm("Delete this event?");
      if (!ok) return;
      try {
        await deleteEvent(article.id);
        setArticles((prev) => {
          const next = prev.filter((a) => a.id !== article.id);
          setStats((st) =>
            st.map((s) => (s.key === "posts" ? { ...s, value: next.length.toLocaleString() } : s))
          );
          return next;
        });
      } catch (e) {
        const msg =
          e instanceof EventApiError
            ? [e.message, ...(e.errors || [])].filter(Boolean).join(" ")
            : e?.message || "Delete failed";
        window.alert(msg);
      }
      return;
    }
    if (type === "edit") {
      navigate(`/admin/posts/${article.id}/edit`);
    }
  };

  return (
    <AdminShell title="Dashboard">
      {error ? (
        <div className="adminCard" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 800, color: "#111827" }}>Unable to load dashboard</div>
          <div style={{ marginTop: 6, color: "#6b7280", fontSize: 13 }}>{error}</div>
          <div style={{ marginTop: 12 }}>
            <button type="button" className="adminButton" onClick={() => window.location.reload()}>
              Retry
            </button>
          </div>
        </div>
      ) : null}

      <div className="adminStatsGrid">{statsContent}</div>

      <div className="adminContentGrid">
        <div className="adminSidebarStack">
          <RecentArticles articles={articles} loading={loading} onArticleAction={handleArticleAction} />
        </div>
        <div className="adminSidebarStack">
          <DashboardMessagesCard items={messagePreview} loading={loading} />
          <DashboardVolunteersCard items={volunteerPreview} loading={loading} />
        </div>
      </div>
    </AdminShell>
  );
}
